import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { API, fetchSite, imgUrl } from "@/lib/api";

export const SITE_NAME = "Halsnøy Kulturhus";
export const DEFAULT_DESCRIPTION =
  "Kulturhuset i Sæbøvik på Halsnøy: amfi med 187 plassar, kafé, selskapslokale og møterom med sjøutsikt. Sjå prisar og kalender, og book rom.";
const DEFAULT_IMAGE = "/og-image.jpg";

/* Parent crumbs for nested routes – everything else gets «Heim › Tittel» automatically. */
const PARENTS = { "/rom/": ["Rom og prisar", "/rom"], "/arrangement/": ["Program", "/arrangement"], "/billett/": ["Program", "/arrangement"] };

const origin = () => (typeof window !== "undefined" ? window.location.origin : "");
const imageId = (ref = "") => (String(ref).match(/\/api\/images\/([0-9a-f-]{36})/i) || [])[1] || null;

/** Absolute URL for a page path or image ref (handles backend-relative `/api/images/...`). */
export const absUrl = (ref, w) => {
  if (!ref) return "";
  if (/^https?:\/\//i.test(ref)) return ref;
  if (ref.startsWith("/api/")) return imgUrl(ref, w);
  return `${origin()}${ref.startsWith("/") ? "" : "/"}${ref}`;
};

/** Server-rendered share page for crawlers that do not run JS (Facebook, LinkedIn, WhatsApp, Slack …). */
export const shareUrl = (kind, id) => `${API}/share/${kind}/${id}`;

/** Composed 1200×630 social card (photo + title + brand). `og` = {kind:'arrangement'|'rom', id} picks the entity card. */
export const ogImage = ({ og, title, subtitle, image }) => {
  if (og?.kind && og?.id) return `${API}/og/${og.kind}/${encodeURIComponent(og.id)}.jpg`;
  const q = new URLSearchParams();
  if (title) q.set("t", title.slice(0, 120));
  if (subtitle) q.set("s", subtitle.slice(0, 100));
  const id = imageId(image);
  if (id) q.set("img", id);
  return `${API}/og/card.jpg?${q.toString()}`;
};

const upsert = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(attrs.tag || "meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => {
    if (k === "tag") return;
    if (v === null || v === undefined) el.removeAttribute(k);
    else el.setAttribute(k, v);
  });
  return el;
};

const clean = (s = "", max = 160) => {
  const t = String(s).replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max - 1);
  return `${cut.slice(0, Math.max(cut.lastIndexOf(" "), 80))}…`;
};

const setJsonLd = (key, data) => {
  const sel = `script[data-seo="${key}"]`;
  const existing = document.head.querySelector(sel);
  if (!data) { existing?.remove(); return; }
  const el = existing || document.createElement("script");
  el.type = "application/ld+json";
  el.setAttribute("data-seo", key);
  el.textContent = JSON.stringify(data);
  if (!existing) document.head.appendChild(el);
};

/** BreadcrumbList for the current page: Heim › (parent) › title. */
export const breadcrumbJsonLd = (crumbs) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map(([name, path], i) => ({ "@type": "ListItem", position: i + 1, name, ...(path ? { item: `${origin()}${path}` } : {}) })),
});

/**
 * Page metadata: <title>, description, canonical, Open Graph (composed social card), Twitter card, robots,
 * BreadcrumbList and optional page JSON-LD (object or array). Imperative so it co-operates with the static
 * defaults in public/index.html (one tag per name, no duplicates).
 *
 * `og`        – {kind, id} → entity card (/api/og/arrangement/<id>.jpg | /api/og/rom/<slug>.jpg)
 * `image`     – photo used inside the composed card (and as the raw fallback), `subtitle` – eyebrow on the card
 * `rawImage`  – true to use `image` as-is instead of composing a card
 */
export const Seo = ({ title, description, image, imageAlt, subtitle, og, rawImage = false, type = "website", noindex = false, jsonLd, path, breadcrumbs, publishedAt, modifiedAt }) => {
  const { pathname } = useLocation();
  const { data: site } = useQuery({ queryKey: ["site"], queryFn: fetchSite, staleTime: 5 * 60_000 });
  const fullTitle = title ? `${title} – ${SITE_NAME}` : `${SITE_NAME} – huset eigd av folket på Halsnøy`;
  const desc = clean(description || site?.seo_description || DEFAULT_DESCRIPTION);
  const url = `${origin()}${path || pathname}`;
  const cardTitle = title || SITE_NAME;
  const img = rawImage && image ? absUrl(image, 1200) : ogImage({ og, title: cardTitle, subtitle: subtitle || (title ? undefined : "Sæbøvik · Halsnøy"), image: image || site?.seo_image || DEFAULT_IMAGE });
  const ld = jsonLd ? JSON.stringify(jsonLd) : "";
  const crumbs = breadcrumbs || (pathname !== "/" && !noindex
    ? [["Heim", "/"], ...(Object.entries(PARENTS).find(([p]) => pathname.startsWith(p))?.[1] ? [PARENTS[Object.keys(PARENTS).find((p) => pathname.startsWith(p))]] : []), [title || SITE_NAME, null]]
    : null);
  const crumbLd = crumbs ? JSON.stringify(breadcrumbJsonLd(crumbs)) : "";

  useEffect(() => {
    document.title = fullTitle;
    upsert('meta[name="description"]', { name: "description", content: desc });
    upsert('meta[name="robots"]', { name: "robots", content: noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" });
    upsert('link[rel="canonical"]', { tag: "link", rel: "canonical", href: url });

    upsert('meta[property="og:site_name"]', { property: "og:site_name", content: SITE_NAME });
    upsert('meta[property="og:locale"]', { property: "og:locale", content: "nn_NO" });
    upsert('meta[property="og:type"]', { property: "og:type", content: type });
    upsert('meta[property="og:title"]', { property: "og:title", content: title || fullTitle });
    upsert('meta[property="og:description"]', { property: "og:description", content: desc });
    upsert('meta[property="og:url"]', { property: "og:url", content: url });
    upsert('meta[property="og:image"]', { property: "og:image", content: img });
    upsert('meta[property="og:image:secure_url"]', { property: "og:image:secure_url", content: img });
    upsert('meta[property="og:image:type"]', { property: "og:image:type", content: "image/jpeg" });
    upsert('meta[property="og:image:width"]', { property: "og:image:width", content: "1200" });
    upsert('meta[property="og:image:height"]', { property: "og:image:height", content: "630" });
    upsert('meta[property="og:image:alt"]', { property: "og:image:alt", content: imageAlt || `${title || SITE_NAME} – ${SITE_NAME}` });
    upsert('meta[property="article:published_time"]', { property: "article:published_time", content: type === "article" && publishedAt ? publishedAt : null });
    upsert('meta[property="article:modified_time"]', { property: "article:modified_time", content: type === "article" && modifiedAt ? modifiedAt : null });

    upsert('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsert('meta[name="twitter:title"]', { name: "twitter:title", content: title || fullTitle });
    upsert('meta[name="twitter:description"]', { name: "twitter:description", content: desc });
    upsert('meta[name="twitter:image"]', { name: "twitter:image", content: img });
    upsert('meta[name="twitter:image:alt"]', { name: "twitter:image:alt", content: imageAlt || `${title || SITE_NAME} – ${SITE_NAME}` });

    setJsonLd("jsonld", ld ? JSON.parse(ld) : null);
    setJsonLd("breadcrumbs", crumbLd ? JSON.parse(crumbLd) : null);
  }, [fullTitle, desc, url, img, imageAlt, type, noindex, ld, crumbLd, title, publishedAt, modifiedAt]);

  return null;
};

const address = (site) => {
  const addr = site?.address || "Sæbøvik, 5454 Sæbøvik";
  const m = addr.trim().match(/(\d{4})\s+([^\d,]+)$/);
  return { "@type": "PostalAddress", streetAddress: addr.split(",")[0].trim(), postalCode: m ? m[1] : "5454", addressLocality: m ? m[2].trim() : "Sæbøvik", addressRegion: "Vestland", addressCountry: "NO" };
};

/** Organisation + venue + website schema for the front page. */
export const orgJsonLd = (site) => {
  const base = origin();
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${base}/#org`,
        name: site?.org_name || "Halsnøy Kulturhus SA",
        alternateName: SITE_NAME,
        url: base,
        logo: { "@type": "ImageObject", url: `${base}/icon-512.png`, width: 512, height: 512 },
        email: site?.contact_email || undefined,
        telephone: site?.phone || undefined,
        address: address(site),
        sameAs: [site?.facebook_url, site?.instagram_url].filter(Boolean),
      },
      {
        "@type": "WebSite",
        "@id": `${base}/#website`,
        name: SITE_NAME,
        url: base,
        inLanguage: "nn",
        publisher: { "@id": `${base}/#org` },
        creator: site?.credit_show !== false && site?.credit_name ? { "@type": "Organization", name: site.credit_name, url: site.credit_url || undefined, email: site.credit_email || undefined } : undefined,
      },
      {
        "@type": ["PerformingArtsTheater", "EventVenue"],
        "@id": `${base}/#venue`,
        name: SITE_NAME,
        url: base,
        image: `${base}/og-image.jpg`,
        description: site?.seo_description || DEFAULT_DESCRIPTION,
        maximumAttendeeCapacity: 187,
        isAccessibleForFree: false,
        publicAccess: true,
        address: address(site),
        parentOrganization: { "@id": `${base}/#org` },
      },
    ],
  };
};

/** Event schema for a single programme entry. */
export const eventJsonLd = (ev, site) => {
  const base = origin();
  const start = ev.start_time ? `${ev.date}T${ev.start_time}:00` : ev.date;
  const endDate = ev.end_date || ev.date;
  const end = ev.end_time ? `${endDate}T${ev.end_time}:00` : endDate;
  const priceMatch = (ev.price_text || "").match(/(\d[\d\s]*)/);
  const price = priceMatch ? Number(priceMatch[1].replace(/\s/g, "")) : null;
  const free = /gratis/i.test(ev.price_text || "") && price === null;
  return {
    "@context": "https://schema.org",
    "@type": "Event",
    name: ev.title,
    description: ev.summary || ev.description || undefined,
    startDate: start,
    endDate: end,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    image: [ogImage({ og: { kind: "arrangement", id: ev.id } }), ...(ev.image ? [absUrl(ev.image, 1200)] : [])],
    url: `${base}/arrangement/${ev.id}`,
    location: {
      "@type": "Place",
      name: ev.location ? `${SITE_NAME} – ${ev.location}` : SITE_NAME,
      address: address(site),
    },
    organizer: { "@type": "Organization", name: site?.org_name || "Halsnøy Kulturhus SA", url: base },
    performer: ev.performer ? { "@type": "PerformingGroup", name: ev.performer } : undefined,
    offers: ev.price_text || ev.ticket_url || ev.tickets_enabled ? { "@type": "Offer", price: free ? 0 : price ?? undefined, priceCurrency: "NOK", url: ev.ticket_url || `${base}/arrangement/${ev.id}`, availability: "https://schema.org/InStock", validFrom: ev.created_at || undefined } : undefined,
  };
};

/** Venue schema for one room. */
export const roomJsonLd = (room, site) => {
  const base = origin();
  return {
    "@context": "https://schema.org",
    "@type": "EventVenue",
    "@id": `${base}/rom/${room.slug}#venue`,
    name: `${room.name} – ${SITE_NAME}`,
    description: room.description || room.summary || undefined,
    url: `${base}/rom/${room.slug}`,
    image: [ogImage({ og: { kind: "rom", id: room.slug } }), ...(room.images || []).slice(0, 4).map((i) => absUrl(i, 1600))],
    maximumAttendeeCapacity: room.capacity || undefined,
    address: address(site),
    containedInPlace: { "@id": `${base}/#venue` },
    isAccessibleForFree: false,
  };
};
